import { useEffect, useRef, useState } from 'react';

const looks = [
  {
    id: 1,
    title: 'Navy 3-Piece',
    subtitle: 'Slim fit',
    image: 'https://public.readdy.ai/ai/img_res/17988a4b93f18831a7733fafd9746344.jpg'
  },
  {
    id: 2,
    title: 'Charcoal Office Suit',
    subtitle: 'Business essential',
    image: 'https://readdy.ai/api/search-image?query=Sophisticated%20charcoal%20grey%20business%20suit%20on%20mannequin%20in%20premium%20tailoring%20studio%20with%20minimal%20dark%20background%20and%20professional%20lighting%20highlighting%20fabric%20quality%20and%20sharp%20tailoring%20details&width=600&height=800&seq=look-002&orientation=portrait'
  },
  {
    id: 3,
    title: 'Reception Blazer',
    subtitle: 'Wedding ready',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20wedding%20reception%20blazer%20in%20cream%20or%20light%20beige%20color%20on%20mannequin%20in%20luxury%20tailoring%20studio%20with%20soft%20romantic%20lighting%20and%20minimal%20dark%20background%20showcasing%20premium%20fabric%20and%20sophisticated%20tailoring&width=600&height=800&seq=look-003&orientation=portrait'
  },
  {
    id: 4,
    title: 'Crisp White Shirts',
    subtitle: 'Everyday elegance',
    image: 'https://readdy.ai/api/search-image?query=Collection%20of%20crisp%20white%20dress%20shirts%20perfectly%20pressed%20and%20displayed%20on%20mannequins%20in%20luxury%20tailoring%20studio%20with%20clean%20minimal%20dark%20background%20and%20soft%20lighting%20showcasing%20fabric%20quality%20and%20tailoring%20precision&width=600&height=800&seq=look-004&orientation=portrait'
  },
  {
    id: 5,
    title: 'Slim Trousers',
    subtitle: 'Perfect fit',
    image: 'https://readdy.ai/api/search-image?query=Premium%20slim%20fit%20dress%20trousers%20in%20various%20colors%20displayed%20on%20mannequins%20in%20luxury%20tailoring%20studio%20with%20minimal%20dark%20background%20and%20professional%20lighting%20highlighting%20fabric%20drape%20and%20tailoring%20excellence&width=600&height=800&seq=look-005&orientation=portrait'
  },
  {
    id: 6,
    title: 'Ties, Bows & Accessories',
    subtitle: 'Finishing touches',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20collection%20of%20silk%20ties%20bow%20ties%20pocket%20squares%20and%20mens%20accessories%20artfully%20arranged%20on%20dark%20luxury%20surface%20with%20soft%20dramatic%20lighting%20showcasing%20premium%20quality%20and%20sophisticated%20style&width=600&height=800&seq=look-006&orientation=portrait'
  }
];

export default function LooksGallery() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('[data-card-index]');
            cards.forEach((card, index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, index]);
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="looks" ref={sectionRef} className="py-32 px-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
            <span className="text-xs uppercase tracking-[0.2em] text-amber-400 font-medium">
              Portfolio
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Featured Looks
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Explore our collection of premium tailored pieces, crafted with precision and passion
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {looks.map((look, index) => (
            <div
              key={look.id}
              data-card-index={index}
              className={`group cursor-pointer transition-all duration-700 ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="relative overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 hover:border-amber-500/50 transition-all duration-300">
                {/* Image Container */}
                <div className="aspect-[3/4] relative overflow-hidden">
                  <img
                    src={look.image}
                    alt={look.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                  
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Card Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-2xl font-bold mb-1 text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {look.title}
                  </h3>
                  <p className="text-sm text-gray-400 group-hover:text-amber-400 transition-colors">
                    {look.subtitle}
                  </p>
                </div>

                {/* Corner accent */}
                <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-amber-500/0 group-hover:border-amber-500/50 transition-all duration-300 rounded-tr-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
